"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { useMemoizedFn } from "ahooks";
import { useSnapshot } from "valtio";
import { rootStore } from "@store/rootStore";
import {
  SetupChecklistStore,
  refreshChecklistIfStale,
} from "@/src/store/modules/setupChecklist";
import { usePlaidConnections } from "@hooks/plaid/usePlaidConnections";
import { usePlanDiagnosisGoalsQuery } from "@hooks/plan/usePlanDiagnosisGoalsQuery";
import { deriveOnboardingGoalType } from "./deriveOnboardingGoalType";
import { resolveConnectSlot } from "./resolveConnectSlot";
import {
  readConnectSlotDaily,
  writeConnectSlotDaily,
  type ConnectSlotDailyState,
} from "./connectSlotDailyStore";
import type { ChatConnectEntryState } from "./connectEntryState";

/** PRD §4 引言：账户状态超过 3s 未返回按「有数据」处理 */
const ACCOUNT_STATE_TIMEOUT_MS = 3_000;

const deriveHasAccountData = (
  steps: readonly { stepKey: string; completed: boolean }[] | undefined,
): boolean | undefined =>
  steps
    ? steps.some(
        (step) =>
          (step.stepKey === "invest_account" ||
            step.stepKey === "banking_account") &&
          step.completed,
      )
    : undefined;

/**
 * Chat 连接入口状态聚合（EDG-591，fe-spec §2.1 / §3.3 / §3.4）。
 * 全部复用现有取数：起步清单 store、Plaid 连接列表、Plan goals 查询。
 */
export function useChatConnectEntry(): ChatConnectEntryState {
  const { isLoggedIn } = useSnapshot(rootStore.login);
  const { ownerInfo } = useSnapshot(rootStore.user);
  const ownerId = ownerInfo?.id;
  const checklist = useSnapshot(SetupChecklistStore);
  const {
    connections,
    isLoading: plaidLoading,
    error: plaidError,
  } = usePlaidConnections();
  const goalsQuery = usePlanDiagnosisGoalsQuery({
    enabled: Boolean(isLoggedIn),
  });

  const [accountStateTimedOut, setAccountStateTimedOut] = useState(false);
  const [daily, setDaily] = useState<ConnectSlotDailyState>(() =>
    ownerId
      ? readConnectSlotDaily(ownerId)
      : { clickedToday: false, rotationIndex: 0 },
  );
  const exposedRef = useRef(false);

  useEffect(() => {
    if (!isLoggedIn) {
      return;
    }
    void refreshChecklistIfStale();
    const timer = setTimeout(
      () => setAccountStateTimedOut(true),
      ACCOUNT_STATE_TIMEOUT_MS,
    );
    return () => clearTimeout(timer);
  }, [isLoggedIn]);

  useEffect(() => {
    if (ownerId) {
      setDaily(readConnectSlotDaily(ownerId));
    }
  }, [ownerId]);

  const derived = deriveHasAccountData(checklist.data?.steps);
  const hasAccountData = !isLoggedIn
    ? true
    : (derived ?? (checklist.error || accountStateTimedOut ? true : undefined));

  const hasPlaidLink = !isLoggedIn
    ? true
    : plaidError
      ? true
      : plaidLoading
        ? undefined
        : connections.length > 0;

  const goalsSettled = goalsQuery.isSuccess || goalsQuery.isError;
  const goalType = useMemo(
    () => deriveOnboardingGoalType(goalsQuery.data),
    [goalsQuery.data],
  );

  const slot = useMemo(
    () =>
      resolveConnectSlot({
        hasAccountData,
        clickedToday: daily.clickedToday,
        goalsSettled,
        goalType,
        rotationIndex: daily.rotationIndex,
      }),
    [hasAccountData, daily, goalsSettled, goalType],
  );

  /** 每次挂载最多计一次曝光；只写存储不改当前题，避免同屏换题 */
  const markSlotExposed = useMemoizedFn(() => {
    if (!ownerId || exposedRef.current) {
      return;
    }
    exposedRef.current = true;
    writeConnectSlotDaily(ownerId, {
      ...daily,
      rotationIndex: daily.rotationIndex + 1,
    });
  });

  const markSlotClicked = useMemoizedFn(() => {
    if (!ownerId) {
      return;
    }
    const stored = readConnectSlotDaily(ownerId);
    const next = { ...stored, clickedToday: true };
    writeConnectSlotDaily(ownerId, next);
    setDaily(next);
  });

  return {
    hasAccountData,
    hasPlaidLink,
    slot,
    markSlotExposed,
    markSlotClicked,
  };
}
